import { GridSortDirection } from '@mui/x-data-grid';
import { create } from 'zustand';

import { getModuleName } from '@/Modules/ReportsModule/PurchaseOrdersReport/shared/services/purchase-orders-reports.service';
import { mapFiltersToApiQuery } from '@/Modules/ReportsModule/PurchaseOrdersReport/shared/utils/helpers';
import { isErrorInfo } from '@/shared/interfaces/error-info.interface';
import {
  getPurchaseOrdersReportStats,
  searchPurchases
} from '@/shared/services/data/purchases/purchases.data';
import { getTableSorting } from '@/shared/services/helpers/get-table-sorting/get-table-sorting';
import { PurchaseOrderReportUIFilters } from '@/shared/ui-models/purchase-order-report.ui-model';
import { PurchasesTableDataUiModel } from '@/shared/ui-models/purchase.ui-model';
import { PurchaseOrderSearchQuery, PurchasesPayload } from '@/shared/ui-models/purchase.ui-model';

export interface PurchaseOrderReportStats {
  open: number;
  purchased: number;
  canceled: number;
  totalProfitMargin: number;
}

interface State {
  data: PurchasesTableDataUiModel[];
  stats: PurchaseOrderReportStats;
  totalCount: number;
  loading: boolean;
  tableLoading: boolean;
  currentPage: number;
  rowsPerPage: number;
  sortModel: { field: string; sort: GridSortDirection }[];
  query: PurchasesPayload;
  error: string | null;
  searchLoading: boolean;
  dataLoading: boolean;
  actions: {
    getPurchaseOrdersReportStats: (payload: PurchaseOrderSearchQuery) => Promise<void>;
    getPurchaseOrdersReports: (payload: PurchasesPayload) => Promise<void>;
    setCurrentPage: (page: number) => void;
    setRowsPerPage: (rows: number) => void;
    setSortModel: (model: { field: string; sort: GridSortDirection }[]) => void;
    setSearchLoading: (searchLoading: boolean) => void;
    setQuery: (values: PurchaseOrderReportUIFilters, skip?: number) => void;
  };
}

const usePurchaseOrdersReportStore = create<State>((set, get) => ({
  data: [],
  stats: {
    open: 0,
    purchased: 0,
    canceled: 0,
    totalProfitMargin: 0
  },
  totalCount: 0,
  loading: false,
  tableLoading: false,
  currentPage: 1,
  rowsPerPage: 10,
  sortModel: [{ field: 'lastUpdatedAt', sort: 'desc' as GridSortDirection }],
  query: {
    skip: 0,
    limit: 10,
    sort: getTableSorting([{ field: 'lastUpdatedAt', sort: 'desc' }])[0]
  },
  error: null,
  searchLoading: false,
  dataLoading: false,
  actions: {
    getPurchaseOrdersReportStats: async (payload) => {
      set({ loading: true, error: null, tableLoading: true });
      try {
        const res = await getPurchaseOrdersReportStats(payload, getModuleName());
        if (isErrorInfo(res)) {
          set({ error: res.errorMessage });
        } else {
          set({ stats: res.data });
        }
      } catch (error) {
        console.error('Error fetching purchase orders report stats:', error);
        set({ error: 'Failed to fetch purchase orders report stats' });
      } finally {
        set({ loading: false, tableLoading: false });
      }
    },
    getPurchaseOrdersReports: async (payload) => {
      set({ loading: true, error: null, tableLoading: true });
      try {
        const res = await searchPurchases(payload, getModuleName());
        if (isErrorInfo(res)) {
          set({ error: res.errorMessage });
        } else {
          set({ data: res.data, totalCount: res.total_count });
        }
      } catch (error) {
        console.error('Error fetching purchase orders report:', error);
        set({ error: 'Failed to fetch purchase orders report' });
      } finally {
        set({ loading: false, tableLoading: false });
      }
    },
    setCurrentPage: (page) => set({ currentPage: page }),
    setRowsPerPage: (rows) => set({ rowsPerPage: rows }),
    setSortModel: (model) => set({ sortModel: model }),
    setSearchLoading: (searchLoading: boolean) => set({ searchLoading }),
    setQuery: (values, skip?) => {
      const { currentPage, rowsPerPage, sortModel } = get();
      // Use the new mapping helper
      const queryParams = mapFiltersToApiQuery(values);

      // Calculate pagination
      const skipRows = skip === 0 ? 0 : (currentPage - 1) * Number(rowsPerPage);
      const newCurrentPage = skip === 0 ? 1 : currentPage;

      // Update store state
      set({
        query: {
          skip: skipRows,
          limit: Number(rowsPerPage),
          sort: getTableSorting(sortModel)[0],
          ...(Object.keys(queryParams).length && { query: queryParams })
        },
        currentPage: newCurrentPage,
        rowsPerPage: Number(rowsPerPage)
      });
    }
  }
}));

export default usePurchaseOrdersReportStore;
